import React, { useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Nav,
    OverlayTrigger,
    Row,
    Tab,
    Table,
    Tooltip,
} from 'react-bootstrap';
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiFilter,
    FiMail,
    FiMinusCircle,
    FiPhone,
    FiSearch,
    FiUsers,
    FiXCircle,
} from 'react-icons/fi';
import { useTranslation } from '../../hooks/useTranslation';
import { getEmailListItemLabel } from '../../constants/emailListItems';
import { formatSubscriptionEmailReason } from '../../utils/tripsPoliciesImportResult';
import {
    BANK_DETAILS_REMINDER_CODES,
    filterReminderRows,
    formatReminderLocaleDate,
    getStandardRemindersAlertVariant,
    normalizeEmailList,
    normalizeRemindersEmailResult,
    paginateReminderRows,
    sortReminderRows,
} from '../../utils/bankDetailsRemindersResult';
import './BankDetailsRemindersResult.css';

const PAGE_SIZES = [10, 20, 50, 100];

const StatCard = ({ icon: Icon, tone, label, value }) => (
    <Card className="reminders-stat-card">
        <Card.Body className="d-flex align-items-center gap-3">
            <span className={`reminders-stat-icon ${tone}`} aria-hidden>
                <Icon />
            </span>
            <div>
                <div className="text-muted small">{label}</div>
                <div className="reminders-stat-value">{value}</div>
            </div>
        </Card.Body>
    </Card>
);

const SortableHeader = ({ label, sortKey, currentSortKey, sortDirection, onSort }) => {
    const isActive = currentSortKey === sortKey;
    const indicator = !isActive ? '' : sortDirection === 'asc' ? ' ↑' : ' ↓';
    return (
        <th
            className="sortable"
            onClick={() => onSort(sortKey)}
            aria-sort={isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
            {label}{indicator}
        </th>
    );
};

const RemindersDataTable = ({
    rows,
    columns,
    searchKeys,
    searchPlaceholder,
    emptyMessage,
    prefix,
}) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState(columns[0]?.sortKey || null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const processedRows = useMemo(() => {
        const filtered = filterReminderRows(rows, search, searchKeys);
        const column = columns.find((col) => col.sortKey === sortKey);
        const sorted = sortReminderRows(filtered, sortKey, sortDirection, column?.sortAccessor);
        return sorted;
    }, [rows, search, searchKeys, sortKey, sortDirection, columns]);

    const pagination = useMemo(
        () => paginateReminderRows(processedRows, page, pageSize),
        [processedRows, page, pageSize]
    );

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDirection('asc');
        }
        setPage(1);
    };

    if (!rows.length) {
        return <div className="reminders-empty">{emptyMessage}</div>;
    }

    return (
        <>
            <div className="reminders-table-toolbar">
                <Form.Group className="flex-grow-1" style={{ minWidth: '220px', maxWidth: '360px' }}>
                    <Form.Label className="small mb-1">{t(`${prefix}.search`)}</Form.Label>
                    <div className="position-relative">
                        <FiSearch className="position-absolute text-muted" style={{ left: 10, top: 10 }} />
                        <Form.Control
                            type="search"
                            value={search}
                            placeholder={searchPlaceholder}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            style={{ paddingLeft: '2rem' }}
                        />
                    </div>
                </Form.Group>
                <Form.Group style={{ width: '110px' }}>
                    <Form.Label className="small mb-1">{t(`${prefix}.pageSize`)}</Form.Label>
                    <Form.Select
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(1);
                        }}
                    >
                        {PAGE_SIZES.map((size) => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </div>

            <div className="reminders-table-wrap">
                <Table striped hover responsive size="sm" className="align-middle">
                    <thead className="table-light">
                        <tr>
                            {columns.map((column) => (
                                column.sortable === false ? (
                                    <th key={column.key}>{column.label}</th>
                                ) : (
                                    <SortableHeader
                                        key={column.key}
                                        label={column.label}
                                        sortKey={column.sortKey}
                                        currentSortKey={sortKey}
                                        sortDirection={sortDirection}
                                        onSort={handleSort}
                                    />
                                )
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pagination.items.map((row, index) => (
                            <tr key={row._rowId || `${row.reservation_number || 'row'}-${index}`}>
                                {columns.map((column) => (
                                    <td key={column.key}>{column.render(row)}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
                <span className="small text-muted">
                    {t(`${prefix}.pagination`, {
                        from: pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1,
                        to: Math.min(pagination.page * pagination.pageSize, pagination.total),
                        total: pagination.total,
                    })}
                </span>
                <div className="d-flex gap-2">
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={pagination.page <= 1}
                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                    >
                        {t(`${prefix}.prev`)}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={pagination.page >= pagination.totalPages}
                        onClick={() => setPage((prev) => Math.min(pagination.totalPages, prev + 1))}
                    >
                        {t(`${prefix}.next`)}
                    </Button>
                </div>
            </div>
        </>
    );
};

const BankDetailsRemindersResult = ({ result, onDismiss }) => {
    const { t, language } = useTranslation();
    const prefix = 'pages.adminTripsDaily.reminders.result';
    const locale = language === 'en' ? 'en-GB' : 'fr-FR';

    const normalized = useMemo(() => normalizeRemindersEmailResult(result), [result]);
    if (!normalized) return null;

    const formatDate = (value) => formatReminderLocaleDate(value, locale);

    if (normalized.format === 'standard') {
        const { success, code, message, timestamp, filters, summary, details } = normalized;
        const variant = getStandardRemindersAlertVariant(normalized);
        const BannerIcon = variant === 'success' ? FiCheckCircle
            : variant === 'warning' ? FiAlertTriangle
                : FiXCircle;
        const allSkippedNoSent = summary.emailsSent === 0
            && (summary.emailsSkipped > 0 || summary.emailsSkippedEmailList > 0);
        const codeTitleKey = `${prefix}.codes.${code}`;
        const codeTitle = t(codeTitleKey);
        const displayTitle = codeTitle !== codeTitleKey
            ? codeTitle
            : (code || (success ? t(`${prefix}.bannerSuccess`) : t(`${prefix}.bannerError`)));

        const translateRowStatus = (status) => {
            const key = `${prefix}.rowStatus.${status}`;
            const label = t(key);
            return label !== key ? label : (status || '—');
        };

        const statusBadge = (status) => {
            if (status === 'sent') return 'success';
            if (status === 'skipped') return 'secondary';
            if (status === 'failed') return 'danger';
            return 'secondary';
        };

        const detailRows = details.map((row, index) => ({
            ...row,
            _rowId: `${row.policyCode || 'policy'}-${row.emailCode || index}`,
            reasonLabel: formatSubscriptionEmailReason(row.reason || row.message, getEmailListItemLabel),
            emailCodeLabel: row.emailCode ? getEmailListItemLabel(row.emailCode) : '',
        }));

        const detailColumns = [
            {
                key: 'policyCode',
                label: t(`${prefix}.columns.policyCode`),
                sortKey: 'policyCode',
                render: (row) => <code className="small">{row.policyCode || '—'}</code>,
            },
            {
                key: 'status',
                label: t(`${prefix}.columns.status`),
                sortKey: 'status',
                render: (row) => (
                    <Badge bg={statusBadge(row.status)}>{translateRowStatus(row.status)}</Badge>
                ),
            },
            {
                key: 'emailCode',
                label: t(`${prefix}.columns.emailCode`),
                sortKey: 'emailCode',
                render: (row) => (
                    row.emailCode ? (
                        <div className="small">
                            <div>{row.emailCodeLabel}</div>
                            <code className="text-muted">{row.emailCode}</code>
                        </div>
                    ) : '—'
                ),
            },
            {
                key: 'reason',
                label: t(`${prefix}.columns.message`),
                sortKey: 'reason',
                render: (row) => <span className="small text-break">{row.reasonLabel}</span>,
            },
        ];

        return (
            <div className="bank-details-reminders-result">
                <Alert variant={variant} className="reminders-banner" dismissible={!!onDismiss} onClose={onDismiss}>
                    <div className="d-flex align-items-start gap-2">
                        <BannerIcon size={22} className="mt-1 flex-shrink-0" aria-hidden />
                        <div className="flex-grow-1">
                            <Alert.Heading className="h5 mb-1">{displayTitle}</Alert.Heading>
                            {message && <p className="mb-2">{message}</p>}
                            {code === BANK_DETAILS_REMINDER_CODES.SENT && summary.emailsFailed > 0 && (
                                <p className="mb-2 small">
                                    <Badge bg="warning" text="dark">{t(`${prefix}.partialWarning`)}</Badge>
                                </p>
                            )}
                            {code === BANK_DETAILS_REMINDER_CODES.SENT && allSkippedNoSent && (
                                <p className="mb-2 small">
                                    <Badge bg="warning" text="dark">{t(`${prefix}.skippedEmailListWarning`)}</Badge>
                                </p>
                            )}
                            {timestamp && (
                                <p className="mb-0 small text-muted">
                                    {t(`${prefix}.timestamp`)}
                                    {': '}
                                    {new Date(timestamp).toLocaleString(locale, { timeZone: 'Europe/Paris' })}
                                </p>
                            )}
                        </div>
                    </div>
                </Alert>

                <Card className="mb-3">
                    <Card.Header className="d-flex align-items-center gap-2 bg-white">
                        <FiFilter />
                        <span className="fw-semibold">{t(`${prefix}.filtersTitle`)}</span>
                    </Card.Header>
                    <Card.Body>
                        <dl className="reminders-filters-grid mb-0">
                            <div className="reminders-filter-item">
                                <dt>{t(`${prefix}.filterDateStart`)}</dt>
                                <dd>{formatDate(filters.reminderDateStart)}</dd>
                            </div>
                            <div className="reminders-filter-item">
                                <dt>{t(`${prefix}.filterDateEnd`)}</dt>
                                <dd>{formatDate(filters.reminderDateEnd)}</dd>
                            </div>
                            <div className="reminders-filter-item">
                                <dt>{t(`${prefix}.filterTodayParis`)}</dt>
                                <dd>{formatDate(filters.todayParis)}</dd>
                            </div>
                            <div className="reminders-filter-item">
                                <dt>{t(`${prefix}.filterLatestAllowed`)}</dt>
                                <dd>{formatDate(filters.latestAllowedReminderDateEnd)}</dd>
                            </div>
                            <div className="reminders-filter-item">
                                <dt>{t(`${prefix}.filterMinDays`)}</dt>
                                <dd>{filters.minimumDaysAfterTripEnd ?? '—'}</dd>
                            </div>
                            <div className="reminders-filter-item">
                                <dt>{t(`${prefix}.filterCompany`)}</dt>
                                <dd>{filters.companyName || t(`${prefix}.allPartners`)}</dd>
                            </div>
                        </dl>
                    </Card.Body>
                </Card>

                <Row className="g-3 mb-3">
                    <Col xs={12} sm={6} xl={3}>
                        <StatCard
                            icon={FiUsers}
                            tone="primary"
                            label={t(`${prefix}.statEligible`)}
                            value={summary.eligibleClients}
                        />
                    </Col>
                    <Col xs={12} sm={6} xl={3}>
                        <StatCard
                            icon={FiMail}
                            tone="success"
                            label={t(`${prefix}.statEmails`)}
                            value={summary.emailsSent}
                        />
                    </Col>
                    <Col xs={12} sm={6} xl={3}>
                        <StatCard
                            icon={FiMinusCircle}
                            tone="warning"
                            label={t(`${prefix}.statSkipped`)}
                            value={summary.emailsSkipped}
                        />
                    </Col>
                    <Col xs={12} sm={6} xl={3}>
                        <StatCard
                            icon={FiXCircle}
                            tone="danger"
                            label={t(`${prefix}.statErrors`)}
                            value={summary.emailsFailed}
                        />
                    </Col>
                </Row>

                {summary.emailsSkippedEmailList > 0 && (
                    <Row className="g-3 mb-3">
                        <Col xs={12} sm={6} xl={4}>
                            <StatCard
                                icon={FiMinusCircle}
                                tone="warning"
                                label={t(`${prefix}.statSkippedEmailList`)}
                                value={summary.emailsSkippedEmailList}
                            />
                        </Col>
                    </Row>
                )}

                <Card className="mb-3">
                    <Card.Header className="bg-white fw-semibold">
                        {t(`${prefix}.detailsTitle`)}
                        <Badge bg="secondary" className="ms-2">{details.length}</Badge>
                    </Card.Header>
                    <Card.Body>
                        <RemindersDataTable
                            rows={detailRows}
                            columns={detailColumns}
                            searchKeys={['policyCode', 'status', 'emailCode', 'reason', 'reasonLabel', 'emailCodeLabel', 'partnerId']}
                            searchPlaceholder={t(`${prefix}.searchStandardDetails`)}
                            emptyMessage={t(`${prefix}.emptyDetails`)}
                            prefix={prefix}
                        />
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const { success, message, filters, summary, successful, failed } = normalized;
    const hasErrors = summary.failed > 0;
    const bannerVariant = !success ? 'danger' : hasErrors ? 'warning' : 'success';

    const successRows = successful.map((row, index) => ({
        ...row,
        _rowId: `${row.policy_code || 'policy'}-${row.reservation_number || index}`,
    }));

    const failedRows = failed.map((row, index) => ({
        ...row,
        _rowId: `${row.policy_id || row.policy_code || 'fail'}-${index}`,
    }));

    const successColumns = [
        {
            key: 'reservation_number',
            label: t(`${prefix}.columns.reservationNumber`),
            sortKey: 'reservation_number',
            render: (row) => <code className="small">{row.reservation_number || '—'}</code>,
        },
        {
            key: 'policy_code',
            label: t(`${prefix}.columns.policyCode`),
            sortKey: 'policy_code',
            render: (row) => <code className="small">{row.policy_code || '—'}</code>,
        },
        {
            key: 'client_name',
            label: t(`${prefix}.columns.clientName`),
            sortKey: 'client_name',
            render: (row) => row.client_name || '—',
        },
        {
            key: 'client_email',
            label: t(`${prefix}.columns.emails`),
            sortKey: 'client_email',
            sortAccessor: (row) => normalizeEmailList(row.client_email).join(', '),
            render: (row) => {
                const emails = normalizeEmailList(row.client_email);
                if (!emails.length) return '—';
                return (
                    <div className="reminders-email-tags">
                        {emails.map((email) => (
                            <Badge key={email} bg="light" text="dark" className="border">
                                <FiMail className="me-1" size={12} />
                                {email}
                            </Badge>
                        ))}
                    </div>
                );
            },
        },
        {
            key: 'client_phone',
            label: t(`${prefix}.columns.phone`),
            sortKey: 'client_phone',
            sortAccessor: (row) => row.client_phone || row.client_phone_number || '',
            render: (row) => {
                const phone = row.client_phone || row.client_phone_number;
                if (!phone) return '—';
                return (
                    <span className="reminders-phone">
                        <FiPhone aria-hidden />
                        {phone}
                    </span>
                );
            },
        },
        {
            key: 'language',
            label: t(`${prefix}.columns.language`),
            sortKey: 'language',
            render: (row) => row.language || '—',
        },
        {
            key: 'activity_end_date',
            label: t(`${prefix}.columns.tripEndDate`),
            sortKey: 'activity_end_date',
            render: (row) => formatDate(row.activity_end_date),
        },
        {
            key: 'reminder_date',
            label: t(`${prefix}.columns.reminderDate`),
            sortKey: 'reminder_date',
            render: (row) => formatDate(row.reminder_date),
        },
        {
            key: 'sms_sent',
            label: t(`${prefix}.columns.smsSent`),
            sortKey: 'sms_sent',
            render: (row) => (
                row.sms_sent ? (
                    <Badge bg="success">{t(`${prefix}.smsSentYes`)}</Badge>
                ) : (
                    <Badge bg="secondary">{t(`${prefix}.smsSentNo`)}</Badge>
                )
            ),
        },
        {
            key: 'sms_skip_reason',
            label: t(`${prefix}.columns.smsSkipReason`),
            sortKey: 'sms_skip_reason',
            render: (row) => {
                if (row.sms_sent || !row.sms_skip_reason) return '—';
                const reasonKey = `${prefix}.smsSkipReasons.${row.sms_skip_reason}`;
                const label = t(reasonKey);
                const display = label !== reasonKey ? label : row.sms_skip_reason;
                return (
                    <OverlayTrigger overlay={<Tooltip>{row.sms_skip_reason}</Tooltip>}>
                        <Badge bg="warning" text="dark">{display}</Badge>
                    </OverlayTrigger>
                );
            },
        },
        {
            key: 'message',
            label: t(`${prefix}.columns.message`),
            sortKey: 'message',
            render: (row) => <span className="small text-muted">{row.message || '—'}</span>,
        },
    ];

    const failedColumns = [
        {
            key: 'reservation_number',
            label: t(`${prefix}.columns.reservationNumber`),
            sortKey: 'reservation_number',
            render: (row) => <code className="small">{row.reservation_number || '—'}</code>,
        },
        {
            key: 'client',
            label: t(`${prefix}.columns.client`),
            sortKey: 'policy_code',
            render: (row) => row.client_name || row.policy_code || '—',
        },
        {
            key: 'error',
            label: t(`${prefix}.columns.error`),
            sortKey: 'error',
            render: (row) => (
                <Badge bg="danger">{row.error || t(`${prefix}.unknownError`)}</Badge>
            ),
        },
        {
            key: 'detail',
            label: t(`${prefix}.columns.technicalDetail`),
            sortable: false,
            render: (row) => (
                row.policy_id ? (
                    <OverlayTrigger overlay={<Tooltip>{row.policy_id}</Tooltip>}>
                        <code className="small text-muted">{String(row.policy_id).slice(0, 8)}…</code>
                    </OverlayTrigger>
                ) : '—'
            ),
        },
    ];

    return (
        <div className="bank-details-reminders-result">
            <Alert variant={bannerVariant} className="reminders-banner" dismissible={!!onDismiss} onClose={onDismiss}>
                <div className="d-flex align-items-start gap-2">
                    {bannerVariant === 'success' && <FiCheckCircle size={22} className="mt-1 flex-shrink-0" />}
                    {bannerVariant === 'warning' && <FiAlertTriangle size={22} className="mt-1 flex-shrink-0" />}
                    {bannerVariant === 'danger' && <FiXCircle size={22} className="mt-1 flex-shrink-0" />}
                    <div className="flex-grow-1">
                        <Alert.Heading className="h5 mb-1">
                            {success
                                ? (hasErrors
                                    ? t(`${prefix}.bannerPartial`)
                                    : t(`${prefix}.bannerSuccess`))
                                : t(`${prefix}.bannerError`)}
                        </Alert.Heading>
                        {message && <p className="mb-0 small">{message}</p>}
                        <div className="reminders-banner-summary">
                            <span><strong>{summary.totalPoliciesMatched}</strong> {t(`${prefix}.contractsAnalyzed`)}</span>
                            <span><strong>{summary.successful}</strong> {t(`${prefix}.emailsSent`)}</span>
                            <span><strong>{summary.smsSent}</strong> {t(`${prefix}.smsSent`)}</span>
                            <span><strong>{summary.failed}</strong> {t(`${prefix}.errors`)}</span>
                        </div>
                    </div>
                </div>
            </Alert>

            <Card className="mb-3">
                <Card.Header className="d-flex align-items-center gap-2 bg-white">
                    <FiFilter />
                    <span className="fw-semibold">{t(`${prefix}.filtersTitle`)}</span>
                </Card.Header>
                <Card.Body>
                    <dl className="reminders-filters-grid mb-0">
                        <div className="reminders-filter-item">
                            <dt>{t(`${prefix}.filterDateStart`)}</dt>
                            <dd>{formatDate(filters.reminderDateStart)}</dd>
                        </div>
                        <div className="reminders-filter-item">
                            <dt>{t(`${prefix}.filterDateEnd`)}</dt>
                            <dd>{formatDate(filters.reminderDateEnd)}</dd>
                        </div>
                        <div className="reminders-filter-item">
                            <dt>{t(`${prefix}.filterTodayParis`)}</dt>
                            <dd>{formatDate(filters.todayParis)}</dd>
                        </div>
                        <div className="reminders-filter-item">
                            <dt>{t(`${prefix}.filterCompany`)}</dt>
                            <dd>{filters.companyName || t(`${prefix}.allPartners`)}</dd>
                        </div>
                    </dl>
                </Card.Body>
            </Card>

            <Row className="g-3 mb-4">
                <Col xs={12} sm={6} xl={4}>
                    <StatCard
                        icon={FiUsers}
                        tone="primary"
                        label={t(`${prefix}.statSelected`)}
                        value={summary.totalPoliciesMatched}
                    />
                </Col>
                <Col xs={12} sm={6} xl={4}>
                    <StatCard
                        icon={FiMail}
                        tone="success"
                        label={t(`${prefix}.statEmails`)}
                        value={summary.successful}
                    />
                </Col>
                <Col xs={12} sm={6} xl={4}>
                    <StatCard
                        icon={FiXCircle}
                        tone="danger"
                        label={t(`${prefix}.statErrors`)}
                        value={summary.failed}
                    />
                </Col>
            </Row>

            <Card className="mb-3">
                <Card.Body className="p-0">
                    <Tab.Container defaultActiveKey="success">
                        <Nav variant="tabs" className="px-3 pt-3">
                            <Nav.Item>
                                <Nav.Link eventKey="success">
                                    {t(`${prefix}.tabSuccess`)}
                                    <Badge bg="success" className="reminders-tab-badge">{summary.successful}</Badge>
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="failed">
                                    {t(`${prefix}.tabFailed`)}
                                    <Badge bg="danger" className="reminders-tab-badge">{summary.failed}</Badge>
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <div className="p-3">
                            <Tab.Content>
                                <Tab.Pane eventKey="success">
                                    <RemindersDataTable
                                        rows={successRows}
                                        columns={successColumns}
                                        searchKeys={[
                                            'reservation_number',
                                            'policy_code',
                                            'client_name',
                                            'client_email',
                                            'client_phone',
                                            'client_phone_number',
                                            'language',
                                            'message',
                                            'sms_skip_reason',
                                        ]}
                                        searchPlaceholder={t(`${prefix}.searchSuccess`)}
                                        emptyMessage={t(`${prefix}.emptySuccess`)}
                                        prefix={prefix}
                                    />
                                </Tab.Pane>
                                <Tab.Pane eventKey="failed">
                                    <RemindersDataTable
                                        rows={failedRows}
                                        columns={failedColumns}
                                        searchKeys={['reservation_number', 'policy_code', 'client_name', 'error', 'policy_id']}
                                        searchPlaceholder={t(`${prefix}.searchFailed`)}
                                        emptyMessage={t(`${prefix}.emptyFailed`)}
                                        prefix={prefix}
                                    />
                                </Tab.Pane>
                            </Tab.Content>
                        </div>
                    </Tab.Container>
                </Card.Body>
            </Card>

            <div className="reminders-footer">
                <h6 className="mb-3">{t(`${prefix}.footerTitle`)}</h6>
                <div className="reminders-footer-grid">
                    <div className="reminders-footer-item">
                        <span className="value">{summary.totalPoliciesMatched}</span>
                        <span className="label">{t(`${prefix}.contractsAnalyzed`)}</span>
                    </div>
                    <div className="reminders-footer-item">
                        <span className="value">{summary.successful}</span>
                        <span className="label">{t(`${prefix}.emailsSent`)}</span>
                    </div>
                    <div className="reminders-footer-item">
                        <span className="value">{summary.smsSent}</span>
                        <span className="label">{t(`${prefix}.smsSent`)}</span>
                    </div>
                    <div className="reminders-footer-item">
                        <span className="value">{summary.failed}</span>
                        <span className="label">{t(`${prefix}.errors`)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BankDetailsRemindersResult;
